<template>
  <div id="irm-app" class="irm-app">
    <!-- 预览模式提示 -->
    <div v-if="isPreviewMode" class="preview-banner">
      <span class="preview-text">该页面为预览页！</span>
    </div>
    <router-view />
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { isPreviewMode as checkIsPreviewMode } from "@/composables/useMicroFrontend";
import { useBackgroundStore } from "@/stores/background";
import { useKeyFlag } from "@/composables/useKeyFlag";

interface Props {
  tenantId: string;
  mode?: "standalone" | "embedded";
  showHeader?: boolean;
  showFooter?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  mode: "embedded",
  showHeader: true,
  showFooter: true,
});

const isPreviewMode = computed(() => checkIsPreviewMode());
const backgroundStore = useBackgroundStore();
const { currentKeyFlag } = useKeyFlag();

// 路由重定向/参数就位可能是异步的，因此需要监听 keyFlag 变化
let lastLoadedKeyFlag: string | null = null;
watch(
  () => currentKeyFlag.value,
  async (flag) => {
    if (!flag || flag === lastLoadedKeyFlag) return;
    lastLoadedKeyFlag = flag;
    backgroundStore.setKeyFlag(flag);
    await backgroundStore.loadCompanyInfoImages(flag);
  },
  { immediate: true }
);
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* 预览模式提示 - 左上角、白背景红字 */
.preview-banner {
  position: fixed;
  top: 10px;
  left: 10px;
  background: white;
  color: #ff4d4f;
  padding: 6px 12px;
  z-index: 100;
  font-size: 12px;
  font-weight: bold;
  border: 2px solid #ff4d4f;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(255, 77, 79, 0.2);
}

.preview-text {
  display: inline-block;
}

html,
body {
  height: 100%;
  margin: 0;
  overflow: hidden;
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue",
    Arial, sans-serif;
}

.irm-app {
  width: 100%;
  height: 100%;
  min-height: 100vh;
  background-color: #f5f7fa;
  overflow: hidden;
}

/* 全局CSS变量 - 与原页面保持一致 */
:root {
  --irm-primary-color: #01509b;
  --irm-secondary-color: #67c23a;
  --irm-success-color: #67c23a;
  --irm-warning-color: #e6a23c;
  --irm-danger-color: #f56c6c;
  --irm-info-color: #909399;

  --irm-bg-color: #ffffff;
  --irm-bg-light: #f5f7fa;
  --irm-bg-lighter: #fafafa;

  --irm-text-color: #303133;
  --irm-text-regular: #606266;
  --irm-text-secondary: #909399;
  --irm-text-placeholder: #c0c4cc;

  --irm-border-color: #dcdfe6;
  --irm-border-light: #e4e7ed;
  --irm-border-lighter: #ebeef5;

  --irm-font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --irm-border-radius: 4px;

  --el-color-primary: #01509b;
  --el-color-primary-light-3: #3373af;
  --el-color-primary-light-5: #6696c3;
  --el-color-primary-light-7: #99b9d7;
  --el-color-primary-light-8: #b3cbe1;
  --el-color-primary-light-9: #ccdceb;
  --el-color-primary-dark-2: #01407c;
}

/* Element Plus主题覆盖 */
.el-button--primary {
  background-color: var(--irm-primary-color);
  border-color: var(--irm-primary-color);
}

.el-button--primary:hover,
.el-button--primary:focus {
  background-color: #0066cc;
  border-color: #0066cc;
}

.el-link--primary {
  color: var(--irm-primary-color);
}

.el-tabs__item.is-active {
  color: var(--irm-primary-color);
}

.el-tabs__active-bar {
  background-color: var(--irm-primary-color);
}

.el-carousel__arrow {
  background-color: rgba(31, 45, 61, 0.11);
}

.el-carousel__arrow:hover {
  background-color: rgba(31, 45, 61, 0.23);
}

.el-carousel__button {
  background-color: var(--irm-primary-color);
}

/* 响应式断点 */
@media (max-width: 1200px) {
  .irm-app {
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .irm-app {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .irm-app {
    font-size: 12px;
  }
}

/* 日期范围选择器弹出层：首页与股价页共用（popper 在 body，需全局样式） */
.home-daterange-popper.el-popper,
.home-daterange-popper.el-picker__popper {
  max-height: min(420px, calc(100vh - 80px)) !important;
}
.home-daterange-popper .el-picker-panel__body-wrapper {
  max-height: min(360px, calc(100vh - 140px)) !important;
  overflow-y: auto !important;
}
.home-daterange-popper .el-date-range-picker .el-picker-panel__body {
  max-height: inherit !important;
}

@media (max-width: 768px) {
  .home-daterange-popper.el-popper,
  .home-daterange-popper.el-picker__popper {
    max-width: calc(100vw - 24px) !important;
    width: calc(100vw - 24px) !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    margin-left: 0 !important;
  }
  .home-daterange-popper .el-picker-panel,
  .home-daterange-popper .el-date-range-picker {
    max-width: 100% !important;
    width: 100% !important;
  }
  .home-daterange-popper .el-picker-panel__sidebar {
    display: none !important;
  }
  .home-daterange-popper .el-picker-panel__body,
  .home-daterange-popper .el-picker-panel__body-wrapper {
    min-width: 0 !important;
    max-width: 100% !important;
  }
  .home-daterange-popper .el-date-range-picker .el-picker-panel__body {
    min-width: 0 !important;
    display: block !important;
  }
  .home-daterange-popper .el-date-range-picker__content {
    display: block !important;
    width: 100% !important;
    box-sizing: border-box;
  }
  .home-daterange-popper .el-date-range-picker__content.is-left {
    border-right: none;
    border-bottom: 1px solid var(--el-datepicker-inner-border-color, #ebeef5);
  }
  .home-daterange-popper .el-date-table {
    font-size: 11px;
  }
  .home-daterange-popper .el-date-table th,
  .home-daterange-popper .el-date-table td {
    padding: 1px 0;
    width: 28px;
  }
  .home-daterange-popper .el-date-table .el-date-table-cell {
    height: 24px;
  }
  .home-daterange-popper .el-date-range-picker__header div {
    font-size: 14px;
  }
}
</style>
